import { post, get } from "./api_helper"
import * as url from "./url_helper"

// RFQ Dashboard API Functions

/**
 * Test API connectivity
 * @param {Object} filters - Filter parameters
 * @returns {Promise} API response
 */
export const testRFQDashboardAPI = (filters = {}) => {
  return post(`${url.API_URL}/api/rfq-dashboard-test`, filters)
}

/**
 * Get comprehensive RFQ dashboard overview
 * @param {Object} filters - Filter parameters
 * @returns {Promise} API response
 */
export const getRFQDashboardOverview = (filters = {}) => {
  return post(url.RFQ_DASHBOARD_OVERVIEW, filters)
}

/**
 * Get RFQ status analytics
 * @param {Object} filters - Filter parameters
 * @returns {Promise} API response
 */
export const getRFQStatusAnalytics = (filters = {}) => {
  return post(url.RFQ_STATUS_ANALYTICS, filters)
}

/**
 * Get RFQ milestone analytics
 * @param {Object} filters - Filter parameters
 * @returns {Promise} API response
 */
export const getRFQMilestoneAnalytics = (filters = {}) => {
  return post(url.RFQ_MILESTONE_ANALYTICS, filters)
}

/**
 * Get RFQ financial insights
 * @param {Object} filters - Filter parameters
 * @returns {Promise} API response
 */
export const getRFQFinancialInsights = (filters = {}) => {
  return post(url.RFQ_FINANCIAL_INSIGHTS, filters)
}

/**
 * Get Invoice Analytics graph data for Financial Insights tab
 * @param {Object} params - Parameters including date range
 * @returns {Promise} API response
 */
export const getInvoiceAnalyticsGraphData = (params = {}) => {
  return post(`${url.API_URL}/api/rfq-invoice-analytics-graph-data`, params)
}

/**
 * Get Invoice Analytics details for modal popup
 * @param {Object} params - Parameters including month, invoice_status, pagination
 * @returns {Promise} API response
 */
export const getInvoiceAnalyticsDetails = (params = {}) => {
  return post(`${url.API_URL}/api/rfq-invoice-analytics-details`, params)
}

/**
 * Export Invoice Analytics details to CSV
 * @param {Object} params - Parameters including month, invoice_status, search
 * @returns {Promise} API response
 */
export const exportInvoiceAnalyticsDetails = (params = {}) => {
  return post(`${url.API_URL}/api/rfq-export-invoice-analytics-details`, params, {
    responseType: 'blob',
    headers: {
      'Accept': 'text/csv'
    }
  })
}

/**
 * Get payment records by status for modal view
 * @param {Object} params - Parameters including status, filters, and pagination
 * @returns {Promise} API response
 */
export const getPaymentRecordsByStatus = (params = {}) => {
  return post(url.RFQ_PAYMENT_RECORDS_BY_STATUS, params)
}

/**
 * Get status card details for modal view
 * @param {Object} params - Parameters including type, filters, and pagination
 * @returns {Promise} API response
 */
export const getStatusCardDetails = (params = {}) => {
  return post(url.RFQ_STATUS_CARD_DETAILS, params)
}

/**
 * Export status card details to CSV
 * @param {Object} params - Export parameters including type and filters
 * @returns {Promise} CSV download
 */
export const exportStatusCardDetails = async (params = {}) => {
  try {
    console.log("Exporting status card details to CSV:", params)

    const response = await post(url.RFQ_EXPORT_STATUS_CARD_DETAILS, params, {
      responseType: 'blob',
      headers: {
        'Accept': 'text/csv'
      }
    })

    // Create blob from response
    const blob = new Blob([response], { type: 'text/csv' })

    // Create download link
    const link = document.createElement('a')
    const url_obj = URL.createObjectURL(blob)
    link.setAttribute('href', url_obj)

    // Generate filename
    const filename = `rfq_status_${params.type || 'export'}_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`
    link.setAttribute('download', filename)

    // Trigger download
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    // Clean up the URL
    URL.revokeObjectURL(url_obj)

    console.log("Status card CSV export completed successfully:", {
      filename,
      blobSize: blob.size
    })

  } catch (error) {
    console.error("Status card CSV export failed:", error)
    throw error
  }
}

/**
 * Get RFQ details by type for modal view
 * @param {Object} params - Parameters including type, filters, and pagination
 * @returns {Promise} API response
 */
export const getRFQDetailsByType = (params = {}) => {
  return post(url.RFQ_DETAILS_BY_TYPE, params)
}

/**
 * Get vendor performance analytics
 * @param {Object} filters - Filter parameters
 * @returns {Promise} API response
 */
export const getRFQVendorPerformance = (filters = {}) => {
  return post(url.RFQ_VENDOR_PERFORMANCE, filters)
}

/**
 * Get vendor performance details for modal view
 * @param {Object} params - Parameters including type, vendor_id, pagination
 * @returns {Promise} API response
 */
export const getRFQVendorPerformanceDetails = (params = {}) => {
  return post(url.RFQ_VENDOR_PERFORMANCE_DETAILS, params)
}

/**
 * Get vendor performance summary details for card clicks
 * @param {Object} params - Parameters including type, pagination, search
 * @returns {Promise} API response
 */
export const getRFQVendorPerformanceSummaryDetails = (params = {}) => {
  return post(url.RFQ_VENDOR_PERFORMANCE_SUMMARY_DETAILS, params)
}

/**
 * Get vendor task progress details
 * @param {Object} params - Parameters including vendor_id, pagination, search
 * @returns {Promise} API response
 */
export const getRFQVendorTaskProgressDetails = (params = {}) => {
  return post(url.RFQ_VENDOR_TASK_PROGRESS_DETAILS, params)
}

/**
 * Export vendor performance details to CSV
 * @param {Object} params - Parameters including type, vendor_id, search
 * @returns {Promise} API response
 */
export const exportRFQVendorPerformanceDetails = async (params = {}) => {
  try {
    console.log("Exporting vendor performance details to CSV:", params)

    // First try to get the response without blob to check for errors
    let response
    try {
      response = await post(url.RFQ_EXPORT_VENDOR_PERFORMANCE_DETAILS, params, {
        responseType: 'blob',
        headers: {
          'Accept': 'text/csv'
        }
      })
    } catch (error) {
      console.error("Export API error:", error)
      // If it's a validation error, try to get the JSON response
      if (error.response && error.response.data) {
        const errorText = await error.response.data.text()
        console.error("Error response text:", errorText)
        try {
          const errorJson = JSON.parse(errorText)
          throw new Error(errorJson.message || 'Export failed')
        } catch (parseError) {
          throw new Error(errorText || 'Export failed')
        }
      }
      throw error
    }

    console.log("Vendor performance export response:", response)

    // Create blob from response
    const blob = new Blob([response], { type: 'text/csv' })

    // Create download link
    const link = document.createElement('a')
    const downloadUrl = URL.createObjectURL(blob)
    link.setAttribute('href', downloadUrl)

    // Generate filename
    const filename = `vendor_performance_${params.type || 'export'}_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`
    link.setAttribute('download', filename)

    // Trigger download
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    // Clean up the URL
    URL.revokeObjectURL(downloadUrl)

    console.log("Vendor performance CSV export completed successfully:", {
      filename,
      blobSize: blob.size
    })

    return { status: true, message: 'Export completed successfully' }

  } catch (error) {
    console.error("Vendor performance export failed:", error)
    throw error
  }
}

/**
 * Export vendor performance summary details to CSV
 * @param {Object} params - Parameters including type, search
 * @returns {Promise} API response
 */
export const exportRFQVendorPerformanceSummaryDetails = async (params = {}) => {
  try {
    console.log("Exporting vendor performance summary details to CSV:", params)

    const response = await post(url.RFQ_EXPORT_VENDOR_PERFORMANCE_SUMMARY_DETAILS, params, {
      responseType: 'blob',
      headers: {
        'Accept': 'text/csv'
      }
    })

    console.log("Vendor performance summary export response:", response)

    // Create blob from response
    const blob = new Blob([response], { type: 'text/csv' })

    // Create download link
    const link = document.createElement('a')
    const downloadUrl = URL.createObjectURL(blob)
    link.setAttribute('href', downloadUrl)

    // Generate filename
    const filename = `vendor_performance_summary_${params.type || 'export'}_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`
    link.setAttribute('download', filename)

    // Trigger download
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    // Clean up the URL
    URL.revokeObjectURL(downloadUrl)

    console.log("Vendor performance summary CSV export completed successfully:", {
      filename,
      blobSize: blob.size
    })

    return { status: true, message: 'Export completed successfully' }
  } catch (error) {
    console.error("Vendor performance summary export failed:", error)
    throw error
  }
}

/**
 * Get project analytics
 * @param {Object} filters - Filter parameters
 * @returns {Promise} API response
 */
export const getRFQProjectAnalytics = (filters = {}) => {
  return post(url.RFQ_PROJECT_ANALYTICS, filters)
}

/**
 * Get project analytics details for modal view
 * @param {Object} params - Parameters including type, project_id, pagination
 * @returns {Promise} API response
 */
export const getRFQProjectAnalyticsDetails = (params = {}) => {
  return post(url.RFQ_PROJECT_ANALYTICS_DETAILS, params)
}

/**
 * Export project analytics details to CSV
 * @param {Object} params - Parameters including type, project_id, search
 * @returns {Promise} API response
 */
export const exportRFQProjectAnalyticsDetails = async (params = {}) => {
  try {
    console.log("Exporting project analytics details to CSV:", params)

    const response = await post(url.RFQ_EXPORT_PROJECT_ANALYTICS_DETAILS, params, {
      responseType: 'blob',
      headers: {
        'Accept': 'text/csv'
      }
    })

    console.log("Project analytics CSV export response:", response)

    // Create blob from response
    const blob = new Blob([response], { type: 'text/csv' })

    // Create download link
    const link = document.createElement('a')
    const url_obj = URL.createObjectURL(blob)
    link.setAttribute('href', url_obj)

    // Generate filename
    const filename = `project_analytics_${params.type || 'export'}_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`
    link.setAttribute('download', filename)

    // Trigger download
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    // Clean up the URL
    URL.revokeObjectURL(url_obj)

    console.log("Project analytics CSV export completed successfully:", filename)

    return { status: true, message: 'Export completed successfully' }
  } catch (error) {
    console.error("Project analytics export failed:", error)
    throw error
  }
}

/**
 * Get timeline analytics
 * @param {Object} filters - Filter parameters
 * @returns {Promise} API response
 */
export const getRFQTimelineAnalytics = (filters = {}) => {
  return post(url.RFQ_TIMELINE_ANALYTICS, filters)
}

/**
 * Get timeline analytics details for modal view
 * @param {Object} params - Parameters including type, period, pagination
 * @returns {Promise} API response
 */
export const getRFQTimelineAnalyticsDetails = (params = {}) => {
  return post(url.RFQ_TIMELINE_ANALYTICS_DETAILS, params)
}

/**
 * Export timeline analytics details to CSV
 * @param {Object} params - Parameters including type, period, search
 * @returns {Promise} API response
 */
export const exportRFQTimelineAnalyticsDetails = async (params = {}) => {
  try {
    console.log("Exporting timeline analytics details to CSV:", params)

    const response = await post(url.RFQ_EXPORT_TIMELINE_ANALYTICS_DETAILS, params, {
      responseType: 'blob',
      headers: {
        'Accept': 'text/csv'
      }
    })

    console.log("Timeline analytics CSV export response:", response)

    // Create blob from response
    const blob = new Blob([response], { type: 'text/csv' })

    // Create download link
    const link = document.createElement('a')
    const url_obj = URL.createObjectURL(blob)
    link.setAttribute('href', url_obj)

    // Generate filename
    const filename = `timeline_analytics_${params.type || 'export'}_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`
    link.setAttribute('download', filename)

    // Trigger download
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    // Clean up the URL
    URL.revokeObjectURL(url_obj)

    console.log("Timeline analytics CSV export completed successfully:", filename)

    return { status: true, message: 'Export completed successfully' }
  } catch (error) {
    console.error("Timeline analytics export failed:", error)
    throw error
  }
}

/**
 * Get Good and Service analytics
 * @param {Object} filters - Filter parameters
 * @returns {Promise} API response
 */
export const getRFQGoodServiceAnalytics = (filters = {}) => {
  return post(url.RFQ_GOOD_SERVICE_ANALYTICS, filters)
}

/**
 * Get Good and Service details for modal view
 * @param {Object} params - Parameters including type, pagination, search
 * @returns {Promise} API response
 */
export const getRFQGoodServiceDetails = (params = {}) => {
  return post(url.RFQ_GOOD_SERVICE_DETAILS, params)
}

/**
 * Export Good and Service details to CSV
 * @param {Object} params - Parameters including type, search
 * @returns {Promise} API response
 */
export const exportGoodServiceDetails = async (params = {}) => {
  try {
    console.log("Exporting good service details to CSV:", params)

    const response = await post(url.RFQ_EXPORT_GOOD_SERVICE_DETAILS, params, {
      responseType: 'blob',
      headers: {
        'Accept': 'text/csv'
      }
    })

    console.log("Good service CSV export response:", response)

    // Create blob from response
    const blob = new Blob([response], { type: 'text/csv' })

    // Create download link
    const link = document.createElement('a')
    const url_obj = URL.createObjectURL(blob)
    link.setAttribute('href', url_obj)

    // Generate filename
    const filename = `good_service_${params.type || 'export'}_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`
    link.setAttribute('download', filename)

    // Trigger download
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    // Clean up the URL
    URL.revokeObjectURL(url_obj)

    console.log("Good service CSV export completed successfully:", filename)

    return { status: true, message: 'Export completed successfully' }
  } catch (error) {
    console.error("Good Service export failed:", error)
    throw error
  }
}

/**
 * Get detailed RFQ information
 * @param {number} rfqId - RFQ ID
 * @returns {Promise} API response
 */
export const getRFQDetails = (rfqId) => {
  return get(`${url.RFQ_DETAILS}/${rfqId}`)
}

/**
 * Helper function to format date for API
 * @param {Date} date - Date object
 * @returns {string} Formatted date string (YYYY-MM-DD)
 */
export const formatDateForAPI = (date) => {
  if (!date) return null
  return date.toISOString().split('T')[0]
}

/**
 * Helper function to format date and time for display
 * @param {string|Date} dateTime - Date string or Date object
 * @returns {string} Formatted date time string
 */
export const formatDateTime = (dateTime) => {
  if (!dateTime) return '-'

  try {
    const date = new Date(dateTime)
    if (isNaN(date.getTime())) return '-'

    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })
  } catch (error) {
    console.error('Error formatting date:', error)
    return '-'
  }
}

/**
 * Helper function to get default date range (last 6 months)
 * @returns {Object} Object with start_date and end_date
 */
export const getDefaultDateRange = () => {
  const endDate = new Date()
  const startDate = new Date()
  startDate.setMonth(startDate.getMonth() - 6)

  return {
    start_date: formatDateForAPI(startDate),
    end_date: formatDateForAPI(endDate)
  }
}

/**
 * Helper function to get empty date range (no default dates)
 * @returns {Object} Object with empty start_date and end_date
 */
export const getEmptyDateRange = () => {
  return {
    start_date: '',
    end_date: ''
  }
}

/**
 * Helper function to format currency values
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount) => {
  if (!amount || amount === 0) return '₹0'
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount)
}

/**
 * Helper function to format percentage values
 * @param {number} percentage - Percentage to format
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (percentage) => {
  if (!percentage && percentage !== 0) return '0%'
  return `${parseFloat(percentage).toFixed(1)}%`
}

/**
 * Helper function to get status color based on status name
 * @param {string} status - Status name
 * @returns {string} Bootstrap color class
 */
export const getStatusColor = (status) => {
  const statusColors = {
    'Created': 'info',
    'In Progress': 'warning',
    'Completed': 'success',
    'On Hold': 'secondary',
    'Cancelled': 'danger',
    'Pending': 'warning',
    'Approved': 'success',
    'Rejected': 'danger'
  }
  return statusColors[status] || 'primary'
}

/**
 * Helper function to get chart colors
 * @returns {Array} Array of chart colors
 */
export const getChartColors = () => {
  return [
    '#556ee6', '#34c38f', '#f1b44c', '#50a5f1', '#f46a6a',
    '#74788d', '#343a40', '#6f42c1', '#e83e8c', '#fd7e14'
  ]
}

/**
 * Helper function to process chart data for ApexCharts
 * @param {Array} data - Raw data array
 * @param {string} labelKey - Key for labels
 * @param {string} valueKey - Key for values
 * @returns {Object} Processed chart data
 */
export const processChartData = (data, labelKey, valueKey) => {
  if (!data || !Array.isArray(data)) return { labels: [], series: [] }
  
  return {
    labels: data.map(item => item[labelKey]),
    series: data.map(item => parseFloat(item[valueKey]) || 0)
  }
}

/**
 * Helper function to process timeline data for charts
 * @param {Array} data - Timeline data array
 * @returns {Object} Processed timeline data
 */
export const processTimelineData = (data) => {
  if (!data || !Array.isArray(data)) return { categories: [], series: [] }
  
  return {
    categories: data.map(item => item.period),
    series: [{
      name: 'Total RFQs',
      data: data.map(item => parseInt(item.total_rfqs) || 0)
    }, {
      name: 'Assigned RFQs',
      data: data.map(item => parseInt(item.assigned_rfqs) || 0)
    }]
  }
}

/**
 * Helper function to calculate growth percentage
 * @param {number} current - Current value
 * @param {number} previous - Previous value
 * @returns {Object} Growth data with percentage and trend
 */
export const calculateGrowth = (current, previous) => {
  if (!previous || previous === 0) {
    return { percentage: 0, trend: 'neutral', isPositive: true }
  }
  
  const growth = ((current - previous) / previous) * 100
  return {
    percentage: Math.abs(growth).toFixed(1),
    trend: growth > 0 ? 'up' : growth < 0 ? 'down' : 'neutral',
    isPositive: growth >= 0
  }
}

/**
 * Helper function to get priority color
 * @param {string} priority - Priority level
 * @returns {string} Bootstrap color class
 */
export const getPriorityColor = (priority) => {
  const priorityColors = {
    'High': 'danger',
    'Medium': 'warning',
    'Low': 'success',
    'Critical': 'dark'
  }
  return priorityColors[priority] || 'primary'
}

/**
 * Export payment summary records by status to CSV
 * @param {Object} params - Export parameters
 * @returns {Promise} CSV download
 */
export const exportPaymentSummaryByStatus = async (params = {}) => {
  try {
    console.log("Exporting Payment Summary to CSV:", params)

    let response;
    let endpointUsed = 'payment_summary';

    try {
      // Call the payment summary export endpoint
      response = await post(url.RFQ_EXPORT_PAYMENT_SUMMARY, params, {
        responseType: 'blob',
        headers: {
          'Accept': 'text/csv'
        }
      })

      console.log("Payment Summary export raw response:", response)
      console.log("Response type:", typeof response)
      console.log("Response constructor:", response?.constructor?.name)

      // Handle different response structures
      if (response instanceof Blob) {
        response = { data: response, status: 200 };
      } else if (!response.data || !(response.data instanceof Blob)) {
        console.error("Payment Summary export returned unexpected structure:", response)
        throw new Error("Export endpoint returned invalid CSV data")
      }

    } catch (exportError) {
      console.error("Payment Summary export failed:", exportError.message)
      throw exportError;
    }

    // Validate response
    if (!response || !response.data) {
      throw new Error("No data received from export endpoint")
    }

    const blob = response.data;
    console.log("Payment Summary CSV blob details:", {
      size: blob.size,
      type: blob.type,
      endpoint: endpointUsed
    })

    // Validate blob
    if (blob.size === 0) {
      throw new Error("Received empty CSV file")
    }

    // Create download link
    const link = document.createElement('a')

    // Create download URL
    const url_obj = URL.createObjectURL(blob)
    link.setAttribute('href', url_obj)

    // Generate filename
    const status = params.status || 'all'
    const filename = `payment_summary_${status}_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`
    link.setAttribute('download', filename)

    // Trigger download
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    // Clean up the URL
    URL.revokeObjectURL(url_obj)

    console.log("Payment Summary CSV export completed successfully:", {
      filename,
      endpoint: endpointUsed,
      blobSize: blob.size
    })

    return {
      success: true,
      filename,
      size: blob.size
    }

  } catch (error) {
    console.error("Payment Summary CSV export failed:", error)
    throw error
  }
}

/**
 * Export RFQ records by type to CSV (using axios for consistency)
 * @param {Object} params - Export parameters
 * @returns {Promise} CSV download
 */
export const exportRFQsByType = async (params = {}) => {
  try {
    console.log("Exporting RFQs to CSV:", params)

    let response;
    let endpointUsed = 'stream';

    try {
      // Try the new stream endpoint first
      console.log("Trying stream endpoint...")
      response = await post(url.RFQ_EXPORT_BY_TYPE_STREAM, params, {
        responseType: 'blob',
        headers: {
          'Accept': 'text/csv'
        }
      })

      console.log("Stream endpoint raw response:", response)
      console.log("Response type:", typeof response)
      console.log("Response constructor:", response?.constructor?.name)

      // Handle different response structures
      let csvBlob;
      if (response instanceof Blob) {
        // Direct blob response
        csvBlob = response;
        console.log("Direct blob response detected")
      } else if (response.data instanceof Blob) {
        // Axios-style response with data property
        csvBlob = response.data;
        console.log("Axios-style blob response detected")
      } else if (response.data && response.data.type === 'application/json') {
        console.warn("Stream endpoint returned JSON instead of CSV, reading as text...")
        const text = await response.data.text()
        console.log("JSON response text:", text)

        try {
          const jsonResponse = JSON.parse(text)
          if (jsonResponse.status === false) {
            throw new Error(jsonResponse.message || 'Stream endpoint returned error')
          }
        } catch (parseError) {
          console.warn("Could not parse JSON response:", parseError)
        }

        throw new Error("Stream endpoint returned JSON instead of CSV")
      } else {
        console.error("Unexpected response structure:", response)
        throw new Error("Unexpected response structure")
      }

      // Store the blob in the response object for consistent handling below
      response = { data: csvBlob, status: 200 }

    } catch (streamError) {
      console.warn("Stream endpoint failed, trying original endpoint:", streamError.message)
      endpointUsed = 'original';

      // Fallback to original endpoint
      const originalResponse = await post(url.RFQ_EXPORT_BY_TYPE, params, {
        responseType: 'blob',
        headers: {
          'Accept': 'text/csv'
        }
      })

      // Handle the same response structure issue for original endpoint
      if (originalResponse instanceof Blob) {
        response = { data: originalResponse, status: 200 };
      } else if (originalResponse.data instanceof Blob) {
        response = originalResponse;
      } else {
        console.error("Original endpoint also returned unexpected structure:", originalResponse)
        throw new Error("Both endpoints failed to return valid CSV data")
      }
    }

    console.log("CSV response received:", {
      endpoint: endpointUsed,
      status: response?.status || 'unknown',
      dataType: typeof response?.data,
      dataSize: response?.data?.size || 'unknown',
      hasData: !!response?.data,
      responseStructure: Object.keys(response || {}),
      dataConstructor: response?.data?.constructor?.name,
      blobType: response?.data?.type
    })

    // Validate response
    if (!response || !response.data) {
      console.error("No response or data:", { response, hasResponse: !!response, hasData: !!response?.data })
      throw new Error("No data received from server")
    }

    // Check if data is actually a blob
    if (!(response.data instanceof Blob)) {
      console.error("Response data is not a Blob:", {
        dataType: typeof response.data,
        constructor: response.data?.constructor?.name,
        data: response.data
      })
      throw new Error("Invalid response format - expected CSV blob")
    }

    // Create a blob and download link
    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' })

    // Check if blob is valid
    if (blob.size === 0) {
      throw new Error("Received empty CSV data")
    }

    const link = document.createElement('a')

    // Create download URL
    const url_obj = URL.createObjectURL(blob)
    link.setAttribute('href', url_obj)

    // Generate filename
    const filename = `rfq_${params.type || 'export'}_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`
    link.setAttribute('download', filename)

    // Trigger download
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    // Clean up the URL
    URL.revokeObjectURL(url_obj)

    console.log("CSV export completed successfully:", {
      filename,
      endpoint: endpointUsed,
      blobSize: blob.size
    })

    return { success: true, filename, endpoint: endpointUsed }
  } catch (error) {
    console.error("Error exporting CSV:", error)
    console.error("Error details:", {
      message: error.message,
      response: error.response,
      status: error.response?.status,
      data: error.response?.data
    })

    // If it's an authentication error, show a more user-friendly message
    if (error.response && error.response.status === 401) {
      throw new Error("Authentication failed. Please login again.")
    }

    // If it's a validation error, show the validation message
    if (error.response && error.response.status === 422) {
      const errorMessage = error.response.data?.message || "Invalid parameters provided."
      throw new Error(errorMessage)
    }

    // For other errors, show a more detailed message
    const errorMessage = error.response?.data?.message || error.message || "Unknown error occurred"
    throw new Error(`Failed to export CSV: ${errorMessage}`)
  }
}

/**
 * Helper function to truncate text
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated text
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text) return ''
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text
}

/**
 * Get RFQs by status for modal view
 * @param {string} cardType - Type of card (total, created, wip, closed)
 * @param {Object} filters - Filter parameters
 * @returns {Promise} API response
 */
export const getRFQsByStatus = (cardType, filters = {}) => {
  const params = {
    card_type: cardType,
    ...filters
  }
  return post(url.RFQ_STATUS_CARD_DETAILS, params)
}

/**
 * Export RFQs by status to CSV
 * @param {string} cardType - Type of card (total, created, wip, closed)
 * @param {Object} filters - Filter parameters
 * @returns {Promise} CSV download
 */
export const exportRFQsToCSV = async (cardType, filters = {}) => {
  try {
    console.log("Exporting RFQs to CSV:", { cardType, filters })

    const params = {
      card_type: cardType,
      ...filters
    }

    const response = await post(url.RFQ_EXPORT_STATUS_CARD_DETAILS, params, {
      responseType: 'blob',
      headers: {
        'Accept': 'text/csv'
      }
    })

    console.log("CSV export response:", response)

    // Handle different response structures
    let csvBlob;
    if (response instanceof Blob) {
      csvBlob = response;
    } else if (response.data instanceof Blob) {
      csvBlob = response.data;
    } else {
      throw new Error("Invalid response format - expected CSV blob")
    }

    // Validate blob
    if (csvBlob.size === 0) {
      throw new Error("Received empty CSV file")
    }

    return {
      status: true,
      data: csvBlob
    }

  } catch (error) {
    console.error("CSV export failed:", error)
    throw error
  }
}

/**
 * Get RFQs by specific status name for modal view
 * @param {Object} filters - Filter parameters including status_name
 * @returns {Promise} API response
 */
export const getRFQsByStatusName = (filters = {}) => {
  const params = {
    ...filters
  }
  return post(url.RFQ_STATUS_NAME_DETAILS, params)
}

/**
 * Export RFQs by specific status name for CSV download
 * @param {Object} filters - Filter parameters including status_name
 * @returns {Promise} CSV download
 */
export const exportRFQsByStatusName = async (filters = {}) => {
  try {
    console.log("Exporting RFQs by status name to CSV:", filters)

    const response = await post(url.RFQ_EXPORT_STATUS_NAME_DETAILS, filters, {
      responseType: 'blob'
    })

    console.log("CSV export response:", response)

    // Return the blob data for download
    return {
      status: true,
      data: response
    }

  } catch (error) {
    console.error("CSV export by status name failed:", error)
    throw error
  }
}

/**
 * Get RFQs by specific date for timeline chart drill-down
 * @param {Object} filters - Filter parameters including date
 * @returns {Promise} API response
 */
export const getRFQsByDate = (filters = {}) => {
  const params = {
    ...filters
  }
  return post(url.RFQ_DATE_DETAILS, params)
}

/**
 * Export RFQs by specific date for CSV download
 * @param {Object} filters - Filter parameters including date
 * @returns {Promise} CSV download
 */
export const exportRFQsByDate = async (filters = {}) => {
  try {
    console.log("Exporting RFQs by date to CSV:", filters)

    const response = await post(url.RFQ_EXPORT_DATE_DETAILS, filters, {
      responseType: 'blob'
    })

    console.log("CSV export response:", response)

    // Return the blob data for download
    return {
      status: true,
      data: response
    }

  } catch (error) {
    console.error("CSV export by date failed:", error)
    throw error
  }
}

// Additional Milestones and Tasks Analytics API functions

/**
 * Get additional milestones analytics data
 * @param {Object} filters - Filter parameters
 * @returns {Promise} API response
 */
export const getAdditionalMilestonesAnalytics = (filters = {}) => {
  return post(url.RFQ_ADDITIONAL_MILESTONES_ANALYTICS, filters)
}

/**
 * Get additional tasks analytics data
 * @param {Object} filters - Filter parameters
 * @returns {Promise} API response
 */
export const getAdditionalTasksAnalytics = (filters = {}) => {
  return post(url.RFQ_ADDITIONAL_TASKS_ANALYTICS, filters)
}

/**
 * Get additional milestones details for modal
 * @param {Object} filters - Filter parameters
 * @returns {Promise} API response
 */
export const getAdditionalMilestonesDetails = (filters = {}) => {
  return post(url.RFQ_ADDITIONAL_MILESTONES_DETAILS, filters)
}

/**
 * Get additional tasks details for modal
 * @param {Object} filters - Filter parameters
 * @returns {Promise} API response
 */
export const getAdditionalTasksDetails = (filters = {}) => {
  return post(url.RFQ_ADDITIONAL_TASKS_DETAILS, filters)
}

/**
 * Export additional milestones to CSV
 * @param {Object} filters - Filter parameters
 * @returns {Promise} CSV download
 */
export const exportAdditionalMilestones = async (filters = {}) => {
  try {
    console.log("Exporting additional milestones to CSV:", filters)

    const response = await post(url.RFQ_EXPORT_ADDITIONAL_MILESTONES, filters, {
      responseType: 'blob'
    })

    console.log("Additional milestones CSV export response:", response)

    // Return the blob data for download
    return {
      status: true,
      data: response
    }

  } catch (error) {
    console.error("Additional milestones CSV export failed:", error)
    throw error
  }
}

/**
 * Export additional tasks to CSV
 * @param {Object} filters - Filter parameters
 * @returns {Promise} CSV download
 */
export const exportAdditionalTasks = async (filters = {}) => {
  try {
    console.log("Exporting additional tasks to CSV:", filters)

    const response = await post(url.RFQ_EXPORT_ADDITIONAL_TASKS, filters, {
      responseType: 'blob'
    })

    console.log("Additional tasks CSV export response:", response)

    // Return the blob data for download
    return {
      status: true,
      data: response
    }

  } catch (error) {
    console.error("Additional tasks CSV export failed:", error)
    throw error
  }
}

/**
 * Get rework milestones details for modal
 * @param {Object} filters - Filter parameters
 * @returns {Promise} API response
 */
export const getReworkMilestonesDetails = (filters = {}) => {
  return post(url.RFQ_REWORK_MILESTONES_DETAILS, filters)
}
